import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
import { effectiveGrants, permissionSchema } from './permissions.js';
/**
 * API keys (TS-954): how an integration (RecruitCRM sync, Stripe, a partner) calls the API
 * without a person signing in.
 *
 * - A key belongs to one organisation and acts as its own SERVICE identity — a `users` row of
 *   kind `service` with no membership — so everything it writes is attributed to the key
 *   ("API key: RecruitCRM sync"), never to the admin who created it, and it never appears in
 *   people pickers.
 * - Its SCOPES are permissions from the catalogue, each one the Integration role grants. A
 *   key holds exactly its scopes (organisation-wide), so it reaches a route only when the
 *   route's declared permission is one of them — the same check a person's request gets.
 * - The secret is shown once, on creation or rotation, and stored only as a SHA-256 hash.
 *   Rotation keeps the key's id, name, scopes and identity, so anything that points at the
 *   key (audit history, the outbound webhook subscriptions of TS-958) is untouched; the old
 *   secret can keep working for a grace period so a client can switch without downtime.
 */
/** Every secret starts with this, so a scanner (and the auth plugin) can tell a key from a JWT. */
export const API_KEY_PREFIX = 'crm_';
/** What a key may be granted: the Integration role's permissions, nothing about people or settings. */
export const API_KEY_SCOPES = [
    ...new Set(effectiveGrants('integration').map((g) => g.permission)),
].sort();
export const apiKeyScopesSchema = z
    .array(permissionSchema)
    .min(1, 'choose at least one scope')
    .max(API_KEY_SCOPES.length)
    .refine((s) => s.every((p) => API_KEY_SCOPES.includes(p)), {
    message: `a key's scopes must be among: ${API_KEY_SCOPES.join(', ')}`,
})
    .transform((s) => [...new Set(s)].sort());
/**
 * Rate limits per minute (TS-954). Two buckets, both counted in Redis: one per key, and one per
 * organisation across all its keys (the plan's ceiling). A key may set its own per-minute limit,
 * at most the plan's organisation ceiling; without one it gets the plan's per-key default.
 */
export const PLAN_RATE_LIMITS = {
    trial: { perKey: 60, perOrganisation: 120 },
    standard: { perKey: 300, perOrganisation: 1200 },
    enterprise: { perKey: 1200, perOrganisation: 6000 },
};
export const RATE_LIMIT_WINDOW_SECONDS = 60;
const name = z.string().trim().min(1).max(100);
const rateLimit = z.number().int().min(1).max(100_000).nullable();
export const apiKeyInputSchema = z.object({
    name,
    scopes: apiKeyScopesSchema,
    /** When the key stops working. Null: never (allowed, but the UI suggests an expiry). */
    expiresAt: instantSchema.nullable().default(null),
    /** Requests per minute for this key; null takes the plan's per-key default. */
    rateLimitPerMinute: rateLimit.default(null),
});
/** No defaults: a PATCH changes only what it names. The secret changes only by rotation. */
export const apiKeyPatchSchema = z
    .object({
    name,
    scopes: apiKeyScopesSchema,
    expiresAt: instantSchema.nullable(),
    rateLimitPerMinute: rateLimit,
})
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
export const apiKeyRotateSchema = z.object({
    /** How long the old secret keeps working, so a client can switch. 0 cuts it off at once. */
    graceMinutes: z
        .number()
        .int()
        .min(0)
        .max(7 * 24 * 60)
        .default(60),
});
export const API_KEY_STATUSES = ['active', 'expired', 'revoked'];
export const apiKeySchema = z.object({
    id: idSchema,
    name: z.string(),
    /** The public part of the secret (`crm_ab12cd34`), to recognise a key without revealing it. */
    prefix: z.string(),
    scopes: z.array(permissionSchema),
    status: z.enum(API_KEY_STATUSES),
    expiresAt: instantSchema.nullable(),
    lastUsedAt: instantSchema.nullable(),
    rateLimitPerMinute: z.number().int().nullable(),
    /** The limit actually applied: the key's own, or its plan's per-key default. */
    effectiveRateLimitPerMinute: z.number().int(),
    /** Until when the previous secret still works after a rotation; null when it does not. */
    previousSecretExpiresAt: instantSchema.nullable(),
    rotatedAt: instantSchema.nullable(),
    revokedAt: instantSchema.nullable(),
    createdBy: idSchema.nullable(),
    createdAt: instantSchema,
});
/** Creation and rotation responses: the only time the secret is ever returned. */
export const apiKeyWithSecretSchema = apiKeySchema.extend({ secret: z.string() });
//# sourceMappingURL=api-keys.js.map